import { FrameworkContainer } from "../analystDash.styled";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import { ChartContainer } from "./charts";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { useTheme } from "styled-components";

// Define the type for complianceData
interface FrameworkComplianceData {
  framework: string;
  total_resources: number;
  compliant: number;
  non_compliant: number;
  insufficient_data: number;
}

interface ComplianceDataProps {
    complianceData: FrameworkComplianceData[];
    isRing?: boolean;
    hasNumber?: boolean;
    text: string;
}

// Standard function declaration without React.FC
// Function to convert snake_case to Title Case
function snakeToCapitalized(str: string): string {
  return str
    .split('_') // Split the string by underscores
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize the first letter of each word
    .join(' '); // Join the words with spaces
}

export const CompliancePieCharts = ({ isRing = false, hasNumber = false, text, complianceData }: ComplianceDataProps) => {
  const theme = useTheme();
  
  const inner = isRing ? 50 : 80;
  const outer = isRing ? 65 : 100;
  const hw = isRing ? 140 : 250;
  const c = isRing ? 70 : 80;
  const xy = isRing ? 70 : 80;

  const renderPieChart = (framework, index) => {
    let textCategory;

    const pieData = [
      { name: 'non_compliant', value: framework.non_compliant, color: theme.colors.critical_criticality },
      { name: 'compliant', value: framework.compliant, color: theme.colors.unclassified_criticality },
      { name: 'insufficient_data', value: framework.insufficient_data, color: theme.borders.borderColor }
    ];

    const totalCategoriesLargerThan0 = pieData.filter((category) => category.value !== 0);

    if (hasNumber) {
      textCategory = pieData.find((item) => item.name.toLowerCase() === text.toLowerCase());
    }

    // Calculate percentage of compliant resources (excluding insufficient data)
    const validResources = framework.total_resources - framework.insufficient_data;
    const percentage = validResources > 0 ? Math.round((framework.compliant / validResources) * 100) : 0;

    // Default pie data if no data available
    if (totalCategoriesLargerThan0.length === 0) {
      textCategory = { name: 'No Data', value: 100, color: theme.colors.mediumGrey };
      pieData.push(textCategory);
    }

    return (
      <Grid item xs={12} sm={6} key={index}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1, maxWidth: '100%', overflow: 'hidden' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, minHeight: '24px' }}>
            {framework.framework === "" || framework.framework === "Un Categorised" ? "Unassigned Rules" : framework.framework}
          </Typography>
          <ChartContainer>
            <PieChart width={hw} height={hw}>
              <Pie
                data={pieData}
                cx={c}
                cy={c}
                innerRadius={inner}
                outerRadius={outer}
                stroke=""
                dataKey="value"
              >
                {pieData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={totalCategoriesLargerThan0.length > 0 ? entry.color : theme.colors.mediumGrey} />
                ))}
              </Pie>
              {hasNumber ? (
                <>
                  <text
                    x={75}
                    y={65}
                    fill={theme.colors.textColor}
                    textAnchor="middle"
                    fontSize="1.4em"
                    fontWeight={300}
                    dominantBaseline="central"
                  >
                    <tspan alignmentBaseline="middle">{percentage}%</tspan>
                  </text>
                  <text
                    x={75}
                    y={85}
                    fill={theme.colors.textColor}
                    textAnchor="middle"
                    fontSize="0.9em"
                    fontWeight={300}
                    dominantBaseline="central"
                  >
                    <tspan>{text}</tspan>
                  </text>
                </>
              ) : (
                <text
                  x={xy}
                  y={xy}
                  fill={theme.colors.textColor}
                  textAnchor="middle"
                  fontSize="1em"
                  fontWeight={300}
                  dominantBaseline="central"
                >
                  <tspan>{text}</tspan>
                </text>
              )}
              <Tooltip 
                content={
                  <Paper className="p-2" elevation={1} style={{ backgroundColor: theme.colors.dark, opacity: 0.9 }}>
                    <p>Total Resources: {framework.total_resources}</p>
                    <p>Compliant: {framework.compliant}</p>
                    <p>Non-Compliant: {framework.non_compliant}</p>
                    <p>Insufficient Data: {framework.insufficient_data}</p>
                  </Paper>
                } 
                cursor={{fill: 'transparent'}}
              />
            </PieChart>
          </ChartContainer>
        </Box>
      </Grid>
    );
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Grid container spacing={1}>
        {Array.isArray(complianceData) && complianceData.map(renderPieChart)}
      </Grid>
    </Box>
  );
};
